import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { request } from '../api/client';
import { usePermission, useCanSeeWarehouses } from '../store/auth';

/**
 * Warehouses for a PICKER, and warehouse names for a TABLE — two different questions.
 *
 * A picker should offer only what the viewer may act on and only what is still active, which is exactly
 * `GET /warehouses/my` (scope-aware; everything for unrestricted roles). But a register also has to
 * NAME the warehouses on historical rows, and those can point at a warehouse that has since been
 * deactivated — or, for a Warehouse Manager, at the counterparty warehouse on the other side of a
 * transfer. Resolving labels from the picker list alone would print a raw id there.
 *
 * So: the picker reads the scoped list, and the label map is topped up from the full master list
 * (`active=all`) for viewers who hold WAREHOUSE.VIEW. A Warehouse Manager, who does not, still gets
 * every name their own scope covers — strictly more than before, when the master-list call was simply
 * refused and every name on the page was an id.
 */
export function useWarehouseLookup() {
  const canSeeWarehouses = useCanSeeWarehouses();
  // Both hooks run on every render (rules of hooks); `||` would skip the second whenever the first is true.
  const canViewMaster = usePermission('WAREHOUSE.VIEW');
  const canAdminMaster = usePermission('WAREHOUSE.ADMIN');
  const canReadMaster = canViewMaster || canAdminMaster;

  const { data: mine, isFetched: mineFetched } = useQuery({
    queryKey: ['warehouses-mine'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    enabled: canSeeWarehouses,
    staleTime: 5 * 60_000,
  });

  const { data: all } = useQuery({
    queryKey: ['warehouses-all-incl-inactive'],
    queryFn: () => request<any[]>({ url: '/warehouses', params: { active: 'all' } }),
    enabled: canReadMaster,
    staleTime: 5 * 60_000,
  });

  const byId = useMemo(() => {
    const m = new Map<string, any>();
    for (const w of all ?? []) m.set(String(w.id), w);
    for (const w of mine ?? []) m.set(String(w.id), w);   // scoped list wins where both have it
    return m;
  }, [mine, all]);

  return {
    canSeeWarehouses,
    /** Active, in-scope warehouses — what a picker should offer. */
    options: mine ?? [],
    /** The scoped list has answered (or failed), so `options` is the viewer's real scope, not "not loaded yet". A
     *  one-shot decision about WHICH warehouse is yours must wait for this (DEF-061). */
    scopeKnown: !canSeeWarehouses || mineFetched,
    byId,
    /** `CODE · Name` for a table cell, falling back to `#id` rather than a bare number. */
    labelOf: (id: any) => {
      const w = byId.get(String(id));
      return w ? `${w.code} · ${w.name}` : (id ? `#${id}` : '—');
    },
  };
}
